class GiftOptionsPanel extends HTMLElement {
  constructor() {
    super();
    this.packagingVariantIds = [];
    this.debouncedSaveMessage = this.debounce((event) => this.saveGiftMessage(event), 500);
  }

  connectedCallback() {
    this.toggleButton = this.querySelector('[data-gift-options-toggle]');
    this.panel = this.querySelector('[data-gift-options-panel]');
    this.status = this.querySelector('[data-gift-options-status]');
    this.removeButton = this.querySelector('[data-gift-packaging-remove]');
    this.cardLink = this.querySelector('[data-gift-card-link]');
    this.messageInput = this.querySelector('[data-gift-message]');
    this.packagingHandles = (this.dataset.packagingHandles || '').split(',').filter(Boolean);
    this.packagingVariantIds = this.options.map((option) => option.dataset.packagingVariantId).filter(Boolean);

    this.toggleButton?.addEventListener('click', () => this.togglePanel());
    this.removeButton?.addEventListener('click', () => this.removePackaging());
    this.messageInput?.addEventListener('input', this.debouncedSaveMessage);

    this.options.forEach((option) => {
      option.addEventListener('click', () => this.selectPackaging(option));
    });

    if (this.dataset.selectedVariantId) {
      this.updateSelection(this.dataset.selectedVariantId);
      this.togglePanel(true);
    }
  }

  get options() {
    return Array.from(this.querySelectorAll('[data-gift-packaging-option]'));
  }

  togglePanel(forceOpen) {
    const shouldOpen = typeof forceOpen === 'boolean' ? forceOpen : this.panel.hasAttribute('hidden');
    this.panel.toggleAttribute('hidden', !shouldOpen);
    this.toggleButton.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
    this.toggleButton.textContent = shouldOpen ? 'Hide Gift Options' : 'Add Gift Box';
  }

  async selectPackaging(option) {
    const variantId = option.dataset.packagingVariantId;
    if (!variantId) return;

    this.setLoading(true, 'Updating gift packaging...');

    try {
      await this.clearPackaging();
      await this.addPackaging(variantId);
      await this.refreshCart();
      await this.afterNextPaint();
      this.openMatchingPanels(variantId);
      this.announce('Gift packaging updated.');
    } catch (error) {
      this.announce('Gift packaging could not be updated. Please try again.');
      console.error(error);
    } finally {
      this.setLoading(false);
    }
  }

  async removePackaging() {
    this.setLoading(true, 'Removing gift packaging...');

    try {
      await this.clearPackaging();
      await this.refreshCart();
      await this.afterNextPaint();
      this.openMatchingPanels('');
      this.announce('Gift packaging removed.');
    } catch (error) {
      this.announce('Gift packaging could not be removed. Please try again.');
      console.error(error);
    } finally {
      this.setLoading(false);
    }
  }

  async clearPackaging() {
    const cart = await fetch(`${routes.cart_url}.js`).then((response) => response.json());
    const updates = {};

    cart.items.forEach((item) => {
      if (
        this.packagingVariantIds.includes(String(item.variant_id)) ||
        this.packagingHandles.includes(item.handle) ||
        this.packagingHandles.includes(item.product_handle)
      ) {
        updates[item.key || item.variant_id] = 0;
      }
    });

    if (!Object.keys(updates).length) return cart;

    return fetch(routes.cart_update_url, {
      ...fetchConfig(),
      body: JSON.stringify({ updates }),
    }).then((response) => response.json());
  }

  async addPackaging(variantId) {
    return fetch(routes.cart_add_url, {
      ...fetchConfig(),
      body: JSON.stringify({
        items: [
          {
            id: Number(variantId),
            quantity: 1,
          },
        ],
      }),
    }).then((response) => response.json());
  }

  async saveGiftMessage(event) {
    try {
      await fetch(routes.cart_update_url, {
        ...fetchConfig(),
        body: JSON.stringify({
          attributes: {
            'Gift Message': event.target.value,
          },
        }),
      });
      this.announce('Gift message saved.');
    } catch (error) {
      this.announce('Gift message could not be saved. Please try again.');
      console.error(error);
    }
  }

  async refreshCart() {
    const sectionIds = ['cart-drawer', 'cart-icon-bubble', 'main-cart-items', 'main-cart-footer'];
    const response = await fetch(
      `${routes.cart_url}?sections=${sectionIds.join(',')}&sections_url=${window.location.pathname}`
    );
    const sections = await response.json();

    this.replaceCartDrawerSections(sections['cart-drawer']);
    this.replaceCartIcon(sections['cart-icon-bubble']);
    this.replaceSectionContents('main-cart-items', '.js-contents', sections['main-cart-items']);
    this.replaceSectionContents('main-cart-footer', '.js-contents', sections['main-cart-footer']);
  }

  replaceCartDrawerSections(html) {
    if (!html) return;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const targetItems = document.querySelector('cart-drawer-items');
    const sourceItems = parsed.querySelector('cart-drawer-items');
    const targetTotals = document.querySelector('.drawer__footer .cart-drawer__footer');
    const sourceTotals = parsed.querySelector('.drawer__footer .cart-drawer__footer');

    if (targetItems && sourceItems) {
      targetItems.innerHTML = sourceItems.innerHTML;
      targetItems.className = sourceItems.className;
    }

    if (targetTotals && sourceTotals) {
      targetTotals.innerHTML = sourceTotals.innerHTML;
    }
  }

  replaceCartIcon(html) {
    if (!html) return;

    const target = document.getElementById('cart-icon-bubble');
    const source = new DOMParser().parseFromString(html, 'text/html').getElementById('cart-icon-bubble');

    if (target && source) {
      target.innerHTML = source.innerHTML;
    }
  }

  replaceSectionContents(sectionId, selector, html) {
    if (!html) return;

    const target = document.getElementById(sectionId)?.querySelector(selector);
    const source = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);

    if (target && source) {
      target.innerHTML = source.innerHTML;
    }
  }

  updateSelection(variantId) {
    this.options.forEach((option) => {
      const isSelected = option.dataset.packagingVariantId === String(variantId);
      option.classList.toggle('is-selected', isSelected);
      option.setAttribute('aria-checked', isSelected ? 'true' : 'false');
    });

    this.removeButton?.classList.toggle('is-hidden', !variantId);
    this.cardLink?.classList.toggle('is-hidden', !variantId);
    this.dataset.selectedVariantId = variantId;
  }

  openMatchingPanels(variantId) {
    document.querySelectorAll('gift-options-panel').forEach((panel) => {
      panel.dataset.selectedVariantId = variantId;

      panel.querySelectorAll('[data-gift-packaging-option]').forEach((option) => {
        const isSelected = option.dataset.packagingVariantId === String(variantId);
        option.classList.toggle('is-selected', isSelected);
        option.setAttribute('aria-checked', isSelected ? 'true' : 'false');
      });

      panel.querySelector('[data-gift-packaging-remove]')?.classList.toggle('is-hidden', !variantId);
      panel.querySelector('[data-gift-card-link]')?.classList.toggle('is-hidden', !variantId);

      const optionsPanel = panel.querySelector('[data-gift-options-panel]');
      const toggleButton = panel.querySelector('[data-gift-options-toggle]');

      if (optionsPanel) {
        optionsPanel.hidden = false;
      }

      if (toggleButton) {
        toggleButton.setAttribute('aria-expanded', 'true');
        toggleButton.textContent = 'Hide Gift Options';
      }
    });
  }

  afterNextPaint() {
    return new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });
  }

  setLoading(isLoading, message = '') {
    this.classList.toggle('is-loading', isLoading);
    this.querySelectorAll('button, textarea').forEach((element) => {
      element.toggleAttribute('disabled', isLoading);
    });

    if (message) {
      this.announce(message);
    }
  }

  announce(message) {
    if (this.status) {
      this.status.textContent = message;
    }
  }

  debounce(callback, delay) {
    let timeout;

    return (...args) => {
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => callback.apply(this, args), delay);
    };
  }
}

if (!customElements.get('gift-options-panel')) {
  customElements.define('gift-options-panel', GiftOptionsPanel);
}
